import { useState, useEffect, useCallback } from 'react';

/* ══════════════════════════════════════════════════════════════
   WHO HAS SEEN THE TOUR

   Kept in localStorage keyed by user id rather than on the User
   record. Two reasons: it needs no schema change or endpoint, and
   every existing account is treated as new — which is what we want,
   since nobody has seen this tour before.

   The tradeoff is honest: clearing site data or signing in on a
   second device replays it. That is a one-click skip, not a problem
   worth a migration for.
══════════════════════════════════════════════════════════════ */

const KEY = 'kitsu-tour-done';

/* Account settings has a Replay button but sits far below the shell that
   owns the tour's state. Same pub/sub shape as the mark's celebrate bus,
   for the same reason: threading a callback down through every layer to
   serve one button is not worth a context. */
const requesters = new Set();

export function requestTour() {
  requesters.forEach(fn => fn());
}

const readDone = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; }
  catch (_) { return {}; }
};

export function hasSeenTour(userId) {
  if (!userId) return true;              // nobody signed in: nothing to tour
  return Boolean(readDone()[userId]);
}

export function markTourSeen(userId) {
  if (!userId) return;
  try {
    const all = readDone();
    all[userId] = Date.now();
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch (_) { /* private mode — the tour simply runs again */ }
}

export function useTour(userId) {
  const [open, setOpen] = useState(false);

  /* Auto-run once, and only after there is a user to key it against. */
  useEffect(() => {
    if (!userId) return undefined;
    if (hasSeenTour(userId)) return undefined;
    /* A beat, so the shell has painted and the tour is not measuring a
       half-built page. */
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, [userId]);

  const start = useCallback(() => setOpen(true), []);

  useEffect(() => {
    requesters.add(start);
    return () => { requesters.delete(start); };
  }, [start]);

  const finish = useCallback(() => {
    setOpen(false);
    markTourSeen(userId);
  }, [userId]);

  return { open, start, finish };
}
